import { t } from '@/i18n'
import type { ZReport } from '@/types'

import {
  EmptySeparator,
  If,
  InfoItem,
  Label,
  Section,
  TaxesBreakdown,
  TillBreakdown,
  Total
} from './components'

function payments(report: ZReport) {
  const sortedPayments = Object.keys(report.payments)
    .map(method => ({
      method,
      amount: report.payments[method]
    }))
    .sort((a, b) => b.amount - a.amount)
  let paymentsArray: any[] = []
  for (let i = 0; i < sortedPayments.length; ++i) {
    paymentsArray = [
      ...paymentsArray,
      new InfoItem(
        sortedPayments[i].method.charAt(0).toUpperCase() + sortedPayments[i].method.slice(1),
        sortedPayments[i].amount,
        {
          valueType: 'currency'
        }
      )
    ]
  }
  const totalTips = getTotalTips(report)
  const result = [
    new Section(t('report.payments')),
    ...paymentsArray,
    new EmptySeparator(),
    new InfoItem('*' + t('report.tips'), totalTips, {
      valueType: 'currency'
    })
  ]
  return result
}

function breakdown(reportBreakdown: ZReport['breakdown']) {
  return new TaxesBreakdown(Object.values(reportBreakdown.taxRates), reportBreakdown.taxLabel!)
}

function generateZReport(
  report: ZReport,
  config: { company: { name: string; taxId: string; address: string } }
) {
  return [
    new Label(t('report.z-report'), {
      style: 'bold 30px',
      lineHeight: 35
    }),
    new InfoItem(t('report.from'), report.startDate, {
      valueType: 'date',
      style: '16px'
    }),
    new InfoItem(t('report.to'), report.endDate, {
      valueType: 'date',
      style: '16px'
    }),
    new EmptySeparator(),
    new Label(config.company.name, { style: 'bold 24px', lineHeight: 30 }),
    new Label(config.company.taxId, { style: '16px', lineHeight: 30 }),
    new Label(config.company.address, { style: '16px', lineHeight: 30 }),
    new Section(t('report.general')),
    new InfoItem(t('report.total-tabs'), report.stats.totalTabs),
    new InfoItem(t('report.discounts'), -report.breakdown.discounts, {
      valueType: 'currency'
    }),
    new Section(t('report.till-control')),
    new If(
      !report.multipleShifts,
      new InfoItem(
        t('report.till-fund'),
        Object.values(report.shifts || []).reduce((total, shift) => {
          total += shift.startAmount
          return total
        }, 0),
        {
          valueType: 'currency'
        }
      )
    ),
    new InfoItem(t('report.pay-in'), report.totalIns, {
      valueType: 'currency'
    }),
    new InfoItem(t('report.pay-out'), -report.totalOuts, {
      valueType: 'currency'
    }),
    new TillBreakdown(report.shifts!),
    ...payments(report),
    new EmptySeparator(),
    new If(Object.keys(report.breakdown.taxRates)?.length > 0, breakdown(report.breakdown)),
    new Total(report.breakdown.total, null, getTotalTips(report) > 0 ? 'with-tips' : null)
  ]
}

function getTotalTips(report: ZReport) {
  return Object.values(report.tips).reduce((total, amount) => (total += amount), 0)
}

export default generateZReport
